import { useState, useEffect, useRef } from 'react';

const STEP_ICONS = {
  'Read': '▸', 'Found': '◉', 'Detected': '◉', 'Compared': '⇌',
  'Calculated': '∑', 'Analysed': '◈', 'Suggested': '→', 'Checked': '✓', 'Formed': '◆',
};
const getIcon = step => STEP_ICONS[step.split(' ')[0]] || '·';

const SONNET_IN  = 3.00;    // USD per 1M tokens
const SONNET_OUT = 15.00;
const USD_INR    = 84;

function costINR(inTok, outTok) {
  return ((inTok / 1_000_000) * SONNET_IN + (outTok / 1_000_000) * SONNET_OUT) * USD_INR;
}
function fmtCost(c) { return c < 0.001 ? '<₹0.001' : `₹${c.toFixed(3)}`; }

function TokenStat({ label, value, sub, highlight }) {
  return (
    <div>
      <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#6b7280', letterSpacing: '0.06em', textTransform: 'uppercase', margin: '0 0 2px' }}>{label}</p>
      <p style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 500, color: highlight || '#e5e7eb', margin: '0 0 1px', letterSpacing: '-0.01em' }}>{value}</p>
      <p style={{ fontFamily: 'monospace', fontSize: 10, color: '#6b7280', margin: 0 }}>{sub}</p>
    </div>
  );
}

export default function ChanakyaIntelligencePanel({
  messageId,
  thinkingTrace = [],
  inputTokens = 0,
  outputTokens = 0,
  sessionTotalInputTokens = 0,
  sessionTotalOutputTokens = 0,
  originalPrompt = '',
  onUseImprovedPrompt,
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [coach, setCoach] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [coachErr, setCoachErr] = useState(false);
  const didLoad = useRef(false);

  const msgCost     = costINR(inputTokens, outputTokens);
  const sessionCost = costINR(sessionTotalInputTokens, sessionTotalOutputTokens);
  const totalTok    = inputTokens + outputTokens;
  const sessionTok  = sessionTotalInputTokens + sessionTotalOutputTokens;
  const sessionColor = sessionCost < 0.01 ? '#10b981' : sessionCost < 0.05 ? '#F0B429' : '#ef4444';

  useEffect(() => {
    if (open && !didLoad.current && !coach && !coachLoading) loadCoach();
  }, [open]);

  async function loadCoach() {
    if (!originalPrompt || didLoad.current) return;
    didLoad.current = true;
    setCoachLoading(true); setCoachErr(false);
    try {
      const res = await fetch('/api/chatbot/improve-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ original_prompt: originalPrompt }),
      });
      if (!res.ok) throw new Error();
      setCoach(await res.json());
    } catch { setCoachErr(true); }
    finally { setCoachLoading(false); }
  }

  return (
    <div style={{ marginTop: 6, borderTop: '0.5px solid #374151', paddingTop: 4 }}>
      {/* Collapsed trigger */}
      <button onClick={() => setOpen(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', width: '100%', color: '#6b7280' }}>
        <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#1D9E75', letterSpacing: '0.04em', flexShrink: 0 }}>
          {thinkingTrace.length > 0
            ? thinkingTrace.slice(0, compact ? 2 : 3).join(' → ')
            : 'thinking…'}
        </span>
        <span style={{ flex: 1 }} />
        {totalTok > 0 && <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#6b7280', flexShrink: 0 }}>{totalTok.toLocaleString()}t</span>}
        <svg width={10} height={10} viewBox="0 0 10 10" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0, opacity: 0.4 }}>
          <path d="M2 3.5L5 6.5L8 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>

      {/* Expanded panel */}
      <div style={{ overflow: 'hidden', maxHeight: open ? 600 : 0, transition: 'max-height 0.25s ease', marginTop: open ? 8 : 0 }}>
        <div style={{ background: '#111827', border: '0.5px solid #374151', borderRadius: 8, overflow: 'hidden' }}>

          {/* Section 1: Thinking trace */}
          <div style={{ padding: '10px 12px', borderBottom: '0.5px solid #374151' }}>
            <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#6b7280', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 8px' }}>How Chanakya answered</p>
            <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {thinkingTrace.map((step, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#1D9E75', width: 14, textAlign: 'center', flexShrink: 0 }}>{getIcon(step)}</span>
                  <span style={{ fontSize: 12, color: '#d1d5db', lineHeight: 1.4 }}>{step}</span>
                  {i < thinkingTrace.length - 1 && <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontSize: 9, color: '#374151' }}>↓</span>}
                </li>
              ))}
            </ol>
          </div>

          {/* Section 2: Token meter */}
          <div style={{ padding: '10px 12px', borderBottom: '0.5px solid #374151', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            <TokenStat label="This message" value={`${totalTok.toLocaleString()}t`} sub={fmtCost(msgCost)} />
            <TokenStat label="Session total" value={`${sessionTok.toLocaleString()}t`} sub={fmtCost(sessionCost)} highlight={sessionColor} />
            <TokenStat label="In / Out" value={`${inputTokens}i · ${outputTokens}o`}
              sub={`${outputTokens > 0 ? Math.round((outputTokens / totalTok) * 100) : 0}% output`} />
          </div>

          {/* Section 3: Prompt coach */}
          <div style={{ padding: '10px 12px' }}>
            <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#6b7280', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 8px' }}>Smarter prompt</p>
            {coachLoading && <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Analysing your prompt…</p>}
            {coachErr && <p style={{ fontSize: 12, color: '#6b7280', margin: 0 }}>Coach unavailable.</p>}
            {coach && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <div style={{ background: '#1f2937', borderRadius: 6, padding: 8, border: '0.5px solid #374151' }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#ef4444', margin: '0 0 4px', letterSpacing: '0.06em' }}>− original</p>
                    <p style={{ fontSize: 11, color: '#9ca3af', margin: 0, lineHeight: 1.5 }}>{originalPrompt.length > 80 ? originalPrompt.slice(0, 80) + '…' : originalPrompt}</p>
                  </div>
                  <div style={{ background: '#1f2937', borderRadius: 6, padding: 8, border: '0.5px solid #10b981' }}>
                    <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#10b981', margin: '0 0 4px', letterSpacing: '0.06em' }}>+ rewritten · saves ~{coach.token_saving_pct}%</p>
                    <p style={{ fontSize: 11, color: '#e5e7eb', margin: 0, lineHeight: 1.5, fontWeight: 500 }}>{coach.rewritten}</p>
                  </div>
                </div>
                <p style={{ fontSize: 11, color: '#6b7280', margin: '0 0 8px', fontStyle: 'italic' }}>{coach.reason}</p>
                <button onClick={() => onUseImprovedPrompt?.(coach.rewritten)}
                  style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, background: 'rgba(16,185,129,0.1)', color: '#10b981', border: '0.5px solid #10b981', cursor: 'pointer', fontWeight: 500 }}>
                  Use this prompt ↗
                </button>
              </div>
            )}
            {!coach && !coachLoading && !coachErr && (
              <button onClick={loadCoach}
                style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, background: 'none', color: '#6b7280', border: '0.5px solid #374151', cursor: 'pointer' }}>
                Suggest a better prompt ↗
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
